import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Bell, ArrowUp, ArrowDown, Trash, CheckCircle, XCircle } from "@phosphor-icons/react";

const KIND = {
  stock_in:          { icon: ArrowUp,     tone: "text-emerald-600" },
  stock_out:         { icon: ArrowDown,   tone: "text-rose-600" },
  disposal_request:  { icon: Trash,       tone: "text-amber-600" },
  disposal_marked:   { icon: Trash,       tone: "text-amber-600" },
  disposal_approved: { icon: CheckCircle, tone: "text-emerald-600" },
  disposal_rejected: { icon: XCircle,     tone: "text-rose-600" },
};

function ago(iso) {
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export function NoticeBell() {
  const { user } = useAuth();
  const [data, setData] = useState({ unread: 0, items: [] });
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!user) return;
    let stop = false;
    async function pull() {
      try { const { data } = await api.get("/notifications"); if (!stop) setData(data); } catch { /* silent */ }
    }
    pull();
    const t = setInterval(pull, 15000);
    const onEvt = () => pull();
    window.addEventListener("notices:refresh", onEvt);
    return () => { stop = true; clearInterval(t); window.removeEventListener("notices:refresh", onEvt); };
  }, [user]);

  async function onOpenChange(v) {
    setOpen(v);
    if (v && data.unread > 0) {
      try { await api.post("/notifications/read-all"); setData((d) => ({ ...d, unread: 0, items: d.items.map((i) => ({ ...i, read: true })) })); } catch { /* silent */ }
    }
  }

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="notice-bell-btn"
          className="relative w-9 h-9 flex items-center justify-center rounded-md border border-[hsl(var(--console-border))] text-[hsl(var(--console-fg))] hover:bg-[hsl(var(--console-glow)/0.10)] transition-colors"
          aria-label="Stock notices"
        >
          <Bell size={16} weight={data.unread ? "fill" : "regular"} />
          {data.unread > 0 && (
            <span data-testid="notice-unread-badge" className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[hsl(var(--console-glow))] text-[hsl(var(--console))] text-[10px] font-bold flex items-center justify-center tabular-nums">
              {data.unread > 99 ? "99+" : data.unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" side="right" className="w-[360px] p-0" data-testid="notice-popover">
        <div className="px-4 py-3 border-b border-border flex items-center justify-between">
          <div className="font-heading font-black text-sm">Stock Notices</div>
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Latest {data.items.length}</span>
        </div>
        <div className="max-h-[420px] overflow-y-auto divide-y divide-border">
          {data.items.length === 0 ? (
            <div className="py-10 text-center text-sm text-muted-foreground" data-testid="notice-empty">No stock activity yet.</div>
          ) : data.items.map((n) => {
            const k = KIND[n.kind] || KIND.stock_in;
            const Icon = k.icon;
            return (
              <div key={n.id} className={`px-4 py-3 flex gap-3 ${n.read ? "" : "bg-primary/5"}`} data-testid={`notice-item-${n.id}`}>
                <Icon size={18} weight="fill" className={`${k.tone} shrink-0 mt-0.5`} />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold leading-snug">{n.title}</div>
                  <div className="text-xs text-muted-foreground mt-0.5 leading-snug">{n.body}</div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">{ago(n.created_at)}</div>
                </div>
              </div>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
